<template>
  <div class="statement-view">
    <h2 class="content-block">Statement #{{ id }}</h2>
    <div class="dx-fieldset">
      <div class="dx-fieldset-header">Statement Information</div>
      <div class="dx-field">
        <div class="dx-field-label">Id</div>
        <div class="dx-field-value">{{ id }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Statement Source</div>
        <div class="dx-field-value">
          {{
            importedStatement.statementSourceId &&
              statementSourceLookup[importedStatement.statementSourceId].name
          }}
          ({{ importedStatement.statementSourceId }})
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Uploaded by</div>
        <div class="dx-field-value">
          <user-details
            :user-id="importedStatement.userIdUploaded"
            empty-text="Automatic"
          />
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Statement Format</div>
        <div class="dx-field-value">
          {{
            importedStatement.statementFormatId &&
              statementFormatLookup[importedStatement.statementFormatId].name
          }}
          ({{ importedStatement.statementFormatId }})
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Total Records</div>
        <div class="dx-field-value">
          {{ importedStatement.totalRecords }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">First record</div>
        <div class="dx-field-value">
          <double-time
            :timestamp="importedStatement.firstRecord"
            :time-zone-name="guessTimeZone()"
          />
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Last record</div>
        <div class="dx-field-value">
          <double-time
            :timestamp="importedStatement.lastRecord"
            :time-zone-name="guessTimeZone()"
          />
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Saved</div>
        <div class="dx-field-value">
          {{ formatTimeUserZone(importedStatement.saved, true) }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Imported</div>
        <div class="dx-field-value">
          {{ formatTimeUserZone(importedStatement.imported, true) }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Matched</div>
        <div class="dx-field-value">
          {{ formatTimeUserZone(importedStatement.matched, true) }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Deleted</div>
        <div class="dx-field-value">
          {{ formatTimeUserZone(importedStatement.deleted, true) }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Deleted by</div>
        <div class="dx-field-value">
          <user-details :user-id="importedStatement.userIdDeleted" />
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Raw Data</div>
        <div class="dx-field-value">
          <DxButton icon="save" text="Download" @click="downloadRaw" />
        </div>
      </div>
      <div
        v-if="
          importedStatement.deleted === null &&
            importedStatement.statementFormatId !== null &&
            statementFormatLookup[importedStatement.statementFormatId]
              .allowManualUpload
        "
        class="dx-field"
      >
        <div class="dx-field-label">Delete</div>
        <div class="dx-field-value">
          <DxButton icon="trash" text="Delete" @click="deleteStatement" />
        </div>
      </div>
    </div>

    <dx-data-grid
      :data-source="dataSource"
      key-expr="id"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
      @row-click="showRowContent"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export :enabled="true" :file-name="`statement-${id}`" />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column data-field="id" :format="config.idFormat" />
      <dx-column
        data-field="gateway"
        :lookup="gatewayColumnLookup"
        caption="Gateway"
      />
		<dx-column
        data-field="source"
        :lookup="comparisonSourceTypeColumnLookup"
        caption="Source"
      />
      <dx-column
        data-field="idAtComparisonSource"
        caption="Comparison Source Id"
      />
      <dx-column data-field="idAtGateway" caption="Gateway Id" />
      <dx-column
        data-field="timestamp"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
        caption="Time (user)"
        :sort-index="0"
        sort-order="asc"
      />
      <dx-column
        data-type="datetime"
        caption="Time (statement)"
        cell-template="statement-time-cell-template"
      />
      <template #statement-time-cell-template="{data}">
        <div>
          {{
            formatTime(
              data.data.timestamp,
              gatewayLookup[data.data.gateway].timeZone,
              false,
            )
          }}
        </div>
      </template>
      <dx-column
        data-field="currencyId"
        :lookup="currencyColumnLookup"
        caption="Currency"
      />
      <dx-column data-field="amount" :format="config.financialNumberFormat" />
      <dx-column
        data-field="rowType"
        :lookup="rowTypeColumnLookup"
        caption="Type"
      />
      <dx-column data-field="reversal" true-text="Yes" false-text="No" />
      <dx-column data-field="counterParty" />
      <dx-column
        data-field="deleted"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
        caption="Deleted"
      />
    </dx-data-grid>
  </div>
</template>

<script>
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
	} from 'devextreme-vue/data-grid'
	import {DxButton} from 'devextreme-vue/button'
	import {DxTagBox} from 'devextreme-vue'
	import {http} from '@/utils/http'
	import {config} from '@/config'
	import UserDetails from '@/components/user-details'
	import DoubleTime from '@/components/double-time'
	import {show} from '@/utils/show'
	import {showHttpError} from '../../utils/http'
	import {confirm} from 'devextreme/ui/dialog'
	import {formatTime, formatTimeUserZone, getUserTimeZone} from '@/utils/time'

	export default {
  name: 'ViewStatement',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    UserDetails,
    DoubleTime,
  },
  data() {
    return {
      id: this.$route.params.id,
      config,
      dataSource: null,
      pageSizes: config.itemsPerPage,
      importedStatement: {
        firstRecord: null,
        lastRecord: null,
        imported: null,
        userIdUploaded: null,
        matched: null,
        saved: null,
        statementFormatId: null,
        statementSourceId: null,
        totalRecords: null,
        deleted: null,
        userIdDeleted: null,
      },
      firstRecordStr: null,
      loading: false,
    }
  },
  computed: {
    ...mapState('user', [
      'statementFormatLookup',
      'statementSourceLookup',
      'rowTypeColumnLookup',
      'comparisonSourceColumnLookup',
      'currencyColumnLookup',
      'comparisonSourceLookup',
      'gatewayLookup',
      'gatewayColumnLookup',
      'comparisonSourceTypeColumnLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.loadData()
  },
  methods: {
    formatTimeUserZone,
    formatTime,
    async loadData() {
      const response = await http
        .get('reconciliation/admin/statement/' + this.id)
        .json()
      this.importedStatement = response.importedStatement
      this.dataSource = response.rows
    },
    guessTimeZone() {
      if (this.dataSource === null || this.dataSource.length === 0) {
        return getUserTimeZone()
      } else {
        return this.gatewayLookup[this.dataSource[0].gateway].timeZone
      }
    },
    downloadRaw() {
      http
        .get('reconciliation/admin/statement/rawData/' + this.id, {
          responseType: 'blob',
        })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'statement-' + this.id + '-raw.txt')
          document.body.appendChild(link)
          link.click()
        })
    },
    showRowContent(event) {
      this.$router.push({ path: `/row/${event.data.id}` })
    },
    deleteStatement() {
      confirm(
        '<i>Are you sure you want to delete this statement?</i>',
        'Confirm Deletion',
      ).then((dialogResult) => {
        if (dialogResult) {
          http
            .post('reconciliation/admin/statement/delete', {
              json: {
                id: this.id,
              },
            })
            .then(() => {
              show.success('Statement deleted')
              this.$router.go()
            })
            .catch((e) => {
              showHttpError(e)
            })
        }
      })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}

.dx-button {
  float: left !important;
}
.statement-view .dx-field-label {
  width: 150px;
}
</style>
